import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { SAMPLE_REQUEST_TYPES } from '@/constants/constant';

import ModalContainer from '@/components/modals/ModalContainer';
import ModalHeader from '@/components/modals/ModalHeader';
import ModalFooter from '@/features/sampleDevelopment/components/SampleRequestModals/ModalFooter';
import { useSampleRequestValueChange } from '@/features/sampleDevelopment/hooks/useSampleRequestValueChange';
import {
	useGetSampleRequestByProductIdQuery,
	useSampleUpdateMutation,
	useSubmitSampleRequestMutation,
	useUpdateSampleRequestMutation
} from '@/features/sampleDevelopment/api/sampleDevelopmentApi';
import {
	useGetAllBrandsQuery,
	useGetEnumsQuery,
	useGetNitexOperationListQuery,
	useGetTagsQuery,
	useGetUserByTypeQuery,
	useLazyGetBrandMembersQuery
} from '@/api/commonApis';
// import SampleColorPopUp from '@/components/colors/SampleColorPopUp';
import { checkUserType, packTag, setTag, replaceUnderLine } from '@/utils/Util';
import { fetchSampleProductList } from '@/store/actions/SampleAction';
import { openLoader, closeLoader } from '@/store/actions/LoaderAction';
import Textarea from '@/components/Textarea';
import CommonImage from '@/components/CommonImage';
import { toastError, toastSuccess, toastWarning } from '@/components/Toast';
import LoaderComponent from '@/components/LoaderComponent';
import SendToPdForm from '@/features/sampleDevelopment/components/SampleRequestModals/SendToPdForm';
import SendToFactoryNitexDevForm from '@/features/sampleDevelopment/components/SampleRequestModals/SendToFactoryNitexDevForm';
import SendToFactoryBuyerDevForm from '@/features/sampleDevelopment/components/SampleRequestModals/SendToFactoryBuyerDevForm';
import SendToSampleSectionForm from '@/features/sampleDevelopment/components/SampleRequestModals/SendToSampleSectionForm';
import {
	INITIAL_STATE_FOR_SAMPLE_REQUEST_MODAL,
	SAMPLE_REQUEST_ENUM,
	TECHPACK_STATUS_TAG_COLORS
} from '@/features/sampleDevelopment/constants';
import {
	// applySampleDataToDesigns,
	formatColorResponseList,
	getInitialStateForSampleRequest,
	isCheckListCompleted,
	isSampleDevPurpose,
	isShowProductDetailsOnTable,
	isValidTpHandoverDate,
	// mapSampleDataToColorResponse,
	// updateColors,
	validateFormFields,
	validateSampleDetails,
	validateSampleTypeInTPHandover
} from '@/features/sampleDevelopment/utils';
import TechpackHandover from '@/features/sampleDevelopment/components/SampleRequestModals/TechpackHandover';
import { useProductDevelopmentAddMutation } from '@/features/Scan/api/scanApi';
import ColorSizeQtyModalV2 from '@/components/ColorSizeQtyModalV2';

const SampleRequestModal = ({
	onClose,
	productIds = [],
	setSelectedProdutIds,
	entityType,
	entityId,
	requestType,
	title,
	isEdit = false,
	taskValueType,
	onRefetch = () => {},
	sampleId,
	navigationUrl,
	isFromTechpack = false,
	productActionId = null,
	selectedHandoverUserType,
	handoverModalFor = '',
	isHandoverModal = false,
	onResetError = () => {},
	selectedComment = {}
}) => {
	const {
		errors: inputErrors,
		handleChange,
		requestValues,
		setErrors: setInputErrors,
		setRequestValues
	} = useSampleRequestValueChange(getInitialStateForSampleRequest(requestType));

	const [submitSampleRequest, { isLoading: isSubmitLoading }] = useSubmitSampleRequestMutation();
	const [productDevelopmentAdd, { isLoading: isTPHandoverSubmitting }] = useProductDevelopmentAddMutation();
	const { data: brands } = useGetAllBrandsQuery({ params: { page: 0, size: 1000 } });
	const { data: projectList } = useGetTagsQuery({
		params: { type: 'COLLECTION_CREATION_PURPOSE', page: 0, size: 1000 }
	});
	const { data: sampleTypeList } = useGetEnumsQuery({
		path: SAMPLE_REQUEST_ENUM.SAMPLE_DEVELOPMENT_TYPE_ENUM
	});
	const { data: userList = {} } = useGetUserByTypeQuery(
		{
			params: {
				userTypes: selectedHandoverUserType
				// includeBrandResponse: requestType === SAMPLE_REQUEST_TYPES.TP_HANDOVER_TO_PROJECT_TEAM
			}
		},
		{
			skip: !selectedHandoverUserType
		}
	);

	const { data: nitexOperationList } = useGetNitexOperationListQuery();
	const [sampleUpdate, { isLoading: isSampleUpdateLoading }] = useSampleUpdateMutation();
	const [updateSampleRequest, { isLoading: isUpdateSampleRequestLoading }] =
		useUpdateSampleRequestMutation();
	const { data: sampleRequestData = [] } = useGetSampleRequestByProductIdQuery({
		sampleIds: sampleId
	});

	const [getBrandMembers] = useLazyGetBrandMembersQuery();
	const [brandMembers, setBrandMembers] = useState([]);

	const [selectedDesigns, setSelectedDesigns] = useState([]);
	const [designId, setDesignId] = useState(null);
	const [selectedRowId, setSelectedRowId] = useState(null);
	const [sampleDetailsErrors, setSampleDetailsErrors] = useState([]);
	const sampleStore = useSelector((store) => store.sampleStore);
	const dispatch = useDispatch();
	const navigate = useNavigate();

	useEffect(() => {
		onResetError([]);
	}, []);

	useEffect(() => {
		const brandId = requestValues?.brand?.id;
		if (!brandId || requestType !== SAMPLE_REQUEST_TYPES.TP_HANDOVER_TO_PROJECT_TEAM) return;

		getBrandMembers({ brandId })
			.unwrap()
			.then((res) => {
				const allMembers = Object.values(res || {}).flat();
				setBrandMembers(allMembers);
			})
			.catch(() => {
				setBrandMembers([]);
			});
	}, [requestValues?.brand?.id, requestType]);

	useEffect(() => {
		if (sampleId && isEdit) {
			const getBrand =
				brands?.find((brand) => parseInt(brand?.id) === parseInt(sampleRequestData[0]?.brandId)) ||
				{};
			const destinationCountryIdList = Array.isArray(sampleRequestData[0]?.destinationCountryIdList)
				? sampleRequestData[0].destinationCountryIdList
				: [];
			const getCountry = Array.isArray(nitexOperationList)
				? nitexOperationList
						.filter((item) => destinationCountryIdList.includes(item?.id))
						.map((country) => ({
							...country,
							label: country?.name
						}))
				: [];

			setRequestValues({
				projectName: {
					...sampleRequestData[0]?.projectTagResponse,
					value: sampleRequestData[0]?.projectTagResponse?.text
				},
				sampleType: {
					id: sampleRequestData[0]?.sampleDevelopmentType,
					value: replaceUnderLine(sampleRequestData[0]?.sampleDevelopmentType),
					constant: sampleRequestData[0]?.sampleDevelopmentType
				},
				requiredDate: sampleRequestData[0]?.requireDate,
				sendSampleTo: getCountry,
				expectedOrderDate: sampleRequestData[0]?.expectedOrderDate,
				primaryTargetCustomar: {
					...getBrand,
					value: getBrand?.name,
					name: getBrand?.name
				}
			});
		}
	}, [
		brands?.length,
		nitexOperationList?.length,
		sampleRequestData[0]?.brandId,
		sampleRequestData[0]?.destinationCountryIdList,
		sampleRequestData[0]?.projectName?.text,
		sampleRequestData[0]?.sampleDevelopmentType,
		sampleRequestData[0]?.requireDate,
		sampleRequestData[0]?.expectedOrderDate,
		sampleId?.length,
		isEdit
	]);

	useEffect(() => {
		dispatch(openLoader());
		dispatch(
			fetchSampleProductList(
				productIds,
				requestType === 'SEND_TO_PD' && requestType === 'TP_HANDOVER' ? '' : requestType
			)
		).finally(() => {
			dispatch(closeLoader());
		});
	}, [productIds?.length, requestType]);

	useEffect(() => {
		setSelectedDesigns(sampleStore.selectedProducts.flat());
	}, [sampleStore?.selectedProducts]);

	useEffect(() => {
		const formatSampleResponse = selectedDesigns?.map((item) => {
			const mapSampleList = (source) => {
				const list =
					(source?.lastSampleItemResponse &&
						source.lastSampleItemResponse.sampleItemDetailsResponseList) ||
					[];
				return list.map((sample) => ({
					sampleItemDetailsId: sample?.id,
					...sample,
					...(sample?.colorResponse || {})
				}));
			};

			if (item?.isSet) {
				const setItems = (item?.setItemsList || []).map((member) => {
					const colorResponseList =
						(member?.lastSampleItemResponse?.sampleItemDetailsResponseList &&
							member.lastSampleItemResponse.sampleItemDetailsResponseList.map((sample) => ({
								sampleItemDetailsId: sample?.id,
								...sample,
								...(sample?.colorResponse || {})
							}))) ||
						(item?.lastSampleItemResponse?.sampleItemDetailsResponseList &&
							item.lastSampleItemResponse.sampleItemDetailsResponseList.map((sample) => ({
								sampleItemDetailsId: sample?.id,
								...sample,
								...(sample?.colorResponse || {})
							}))) ||
						[];

					return {
						...member,
						colorResponseList
					};
				});

				return {
					...item,
					setItemsList: setItems
				};
			}

			return {
				...item,
				colorResponseList: mapSampleList(item)
			};
		});

		if (
			!isEdit &&
			!sampleId &&
			requestType !== 'SEND_TO_PD' &&
			requestType !== 'TP_HANDOVER_TO_PROJECT_TEAM'
		) {
			setSelectedDesigns(formatSampleResponse);
		}
	}, [requestType, selectedDesigns?.length, isEdit, sampleId]);

	useEffect(() => {
		if (sampleId?.length > 0 && sampleRequestData?.length > 0 && isEdit) {
			const baseDesigns = sampleStore.selectedProducts.flat();
			if (baseDesigns.length > 0) {
				const formatSampleResponse = baseDesigns?.map((item) => {
					return {
						...item,
						colorResponseList: item?.lastSampleItemResponse?.sampleItemDetailsResponseList?.map(
							(sample) => {
								return {
									sampleItemDetailsId: sample?.id,
									...sample,
									...sample?.colorResponse
								};
							}
						)
					};
				});

				setSelectedDesigns(formatSampleResponse);
			}
		}
	}, [sampleId?.length, sampleRequestData?.length, sampleStore.selectedProducts, isEdit]);

	const handleRemarks = (e) => {
		const updatedDesigns = selectedDesigns.map((item) => {
			if (item.id === selectedRowId) {
				return { ...item, remarks: e.target.value };
			}
			return item;
		});
		setSelectedDesigns(updatedDesigns);
	};

	// Sample details, Color, Size qty, and remarks
	const creteSampleRequestBody = () => {
		const newBody = [];

		selectedDesigns?.forEach((product) => {
			if (product?.isSet) {
				newBody.push({
					id: product.id,
					isSet: true,
					remarks: product.remarks,
					productId: product.id,
					setMembersSampleRequestList: product.setItemsList.map((setMember) => {
						return {
							id: setMember.id,
							// remarks: setMember.remarks,
							productId: setMember.id,
							sampleQuantityList: formatColorResponseList(setMember.colorResponseList)
						};
					})
				});
			} else {
				newBody.push({
					productId: product?.id,
					operationalUnitId: product.operationalUnitId,
					remarks: product?.remarks || '',
					sampleQuantityList: formatColorResponseList(product.colorResponseList)
				});
			}
		});
		return {
			addSampleRequestList: newBody
		};
	};

	const getSetMembersIds = () => {
		const ids = [];

		selectedDesigns?.forEach((product) => {
			if (product?.isSet) {
				product.setItemsList.forEach((item) => {
					ids.push(item?.id);
				});
			} else {
				ids.push(product?.id);
			}
		});
		return ids;
	};

	const handleSuccess = (message = 'Sample request sent successfully') => {
		onRefetch();
		toastSuccess(message);
		setSelectedDesigns([]);
		setRequestValues(INITIAL_STATE_FOR_SAMPLE_REQUEST_MODAL);
		onClose();
	};

	const handleError = (error) => {
		if (error?.data?.status === 400) {
			if (error?.data.returnObject) {
				setSampleDetailsErrors(error?.data.returnObject);
			}
			toastWarning(error?.data?.message);
		} else {
			toastError(error?.data?.message || 'Something went wrong!');
		}
	};

	// Send PD request body
	const createPDRequestBody = () => ({
		entityType,
		entityId,
		expectedOrderDate: requestValues?.expectedOrderDate || null,
		requiredDate: requestValues?.requiredDate || null,
		projectName: requestValues?.projectName || null,
		brandId: requestValues?.primaryTargetCustomar?.id || null,
		sampleDevelopmentType: requestValues?.sampleType?.constant,
		destinationCountryIdList: requestValues?.sendSampleTo?.map((item) => item.id) || null,
		...creteSampleRequestBody(),
		taskValueType: isFromTechpack ? null : taskValueType
	});

	const handleSubmit = () => {
		setSampleDetailsErrors([]);

		const isValidateFields = validateFormFields(requestType, requestValues, setInputErrors);
		const isValidateSampleDetails = validateSampleDetails(selectedDesigns, setSampleDetailsErrors);
		const isCheckListDone = isCheckListCompleted(selectedDesigns, setSampleDetailsErrors);

		if (!isValidateFields) return;
		if (!isValidateSampleDetails) {
			toastWarning('Please input Sample Qty.');
			return;
		}
		if (!isCheckListDone) {
			toastError('Checklist not completed');
			return;
		}

		if (requestType === SAMPLE_REQUEST_TYPES.SEND_TO_PD_IN_HOUSE) {
			if (isEdit) {
				// Update PD request
				updateSampleRequest({
					body: createPDRequestBody(),
					sampleItemId: sampleId
				})
					.unwrap()
					.then((res) => {
						if (!res?.success) {
							if (getSetMembersIds()?.length === 1) {
								const err = Object.values(res?.payload)[0];
								toastError(err || 'Something went wrong');
							} else {
								toastError(res?.message || 'Something went wrong');
							}
							return;
						} else {
							handleSuccess('Sample Request Updated.');
							navigationUrl && navigate(navigationUrl);
						}
					})
					.catch((error) => {
						handleError(error);
					});
			} else {
				// Send to PD
				submitSampleRequest({ body: createPDRequestBody() })
					.unwrap()
					.then((res) => {
						if (!res?.success) {
							if (getSetMembersIds()?.length === 1) {
								const err = Object.values(res?.payload)[0];
								toastError(err || 'Something went wrong');
							} else {
								toastError(res?.message || 'Something went wrong');
							}
							return;
						} else {
							handleSuccess();
						}
					})
					.catch((error) => {
						handleError(error);
					});
			}
		} else if (requestType === SAMPLE_REQUEST_TYPES.SEND_TO_FACTORY_NITEX_DESIGN) {
			// Send to factory nitex dev
			sampleUpdate({
				body: {
					...creteSampleRequestBody(),
					submissionPlanDate: requestValues?.factorySubmitionToNitexDate || null,
					sampleDevelopmentType: requestValues?.sampleType?.constant,
					taskValueType: requestValues?.requestFor?.id,
					developmentFactoryId: requestValues?.factoryName?.id,
					expectedOrderDate: requestValues?.expectedOrderDate || null
				}
			})
				.unwrap()
				.then((res) => {
					if (!res?.success) {
						if (getSetMembersIds()?.length === 1) {
							const err = Object.values(res?.payload)[0];
							toastError(err || 'Something went wrong');
						} else {
							toastError(res?.message || 'Something went wrong');
						}
						return;
					} else {
						handleSuccess();
					}
				})
				.catch((error) => {
					handleError(error);
				});
		} else if (requestType === SAMPLE_REQUEST_TYPES.SEND_TO_FACTORY_BUYER_DESIGN) {
			// send factory test
			if (isEdit) {
				const editBodyData = {
					...creteSampleRequestBody(),
					entityType,
					entityId,
					expectedOrderDate: requestValues?.expectedOrderDate || null,
					submissionPlanDate: requestValues.factorySubmitionToNitexDate || null,
					projectName:
						requestValues?.projectName?.id && requestValues?.projectName?.text
							? {
									id: requestValues?.projectName?.id,
									text: requestValues?.projectName?.text
								}
							: null,
					brandId: requestValues?.brand?.id || null,
					sampleDevelopmentType: requestValues?.sampleType?.constant,
					developmentFactoryId: requestValues?.factoryName?.id,
					taskValueType,
					productActionId
				};
				productDevelopmentAdd({
					body: editBodyData,
					sampleItemId: sampleId
				})
					.unwrap()
					.then((res) => {
						if (!res?.success) {
							if (getSetMembersIds()?.length === 1) {
								const err = Object.values(res?.payload)[0];
								toastError(err || 'Something went wrong');
							} else {
								toastError(res?.message || 'Something went wrong');
							}
							return;
						} else {
							handleSuccess();
							navigationUrl && navigate(navigationUrl);
						}
					})
					.catch((error) => {
						handleError(error);
					});
			} else {
				const createBodyData = {
					handoverItemRequest: {
						...creteSampleRequestBody(),
						sampleType: requestValues?.sampleType?.constant
					},
					entityType,
					entityId,
					expectedOrderDate: requestValues?.expectedOrderDate || null,
					submissionPlanDate: requestValues.factorySubmitionToNitexDate || null,
					projectName:
						requestValues?.projectName?.id && requestValues?.projectName?.text
							? {
									id: requestValues?.projectName?.id,
									text: requestValues?.projectName?.text
								}
							: null,
					brandId: requestValues?.brand?.id || null,
					sampleDevelopmentType: requestValues?.sampleType?.constant,
					developmentFactoryId: requestValues?.factoryName?.id,
					taskValueType,
					productIdList: getSetMembersIds(),
					productActionId
				};
				productDevelopmentAdd({
					body: createBodyData
				})
					.unwrap()
					.then((res) => {
						if (!res?.success) {
							if (getSetMembersIds()?.length === 1) {
								const err = Object.values(res?.payload)[0];
								toastError(err || 'Something went wrong');
							} else {
								toastError(res?.message || 'Something went wrong');
							}
							return;
						} else {
							handleSuccess();
						}
					})
					.catch((error) => {
						handleError(error);
					});
			}
		} else if (requestType === SAMPLE_REQUEST_TYPES.SEND_REVISE_SAMPLE_REQUEST) {
			submitSampleRequest({
				body: {
					...creteSampleRequestBody(),
					entityType,
					entityId,
					sampleDevelopmentType: 'REVISE',
					requiredDate: requestValues?.requiredDate || null,
					taskValueType: SAMPLE_REQUEST_TYPES.SEND_REVISE_SAMPLE_REQUEST
				}
			})
				.unwrap()
				.then((res) => {
					if (!res?.success) {
						if (getSetMembersIds()?.length === 1) {
							const err = Object.values(res?.payload)[0];
							toastError(err || 'Something went wrong');
						} else {
							toastError(res?.message || 'Something went wrong');
						}
						return;
					} else {
						handleSuccess();
					}
				})
				.catch((error) => {
					handleError(error);
				});
		} else if (requestType === SAMPLE_REQUEST_TYPES.SAMPLE) {
			// Send to Sample Section
			sampleUpdate({
				body: {
					...creteSampleRequestBody(),
					requiredDate: requestValues?.requiredDate || null,
					sampleDevelopmentType: requestValues?.sampleType?.constant,
					taskValueType: 'SEND_TO_SAMPLE_SECTION'
				}
			})
				.unwrap()
				.then((res) => {
					if (!res?.success) {
						if (getSetMembersIds()?.length === 1) {
							const err = Object.values(res?.payload)[0];
							toastError(err || 'Something went wrong');
						} else {
							toastError(res?.message || 'Something went wrong');
						}
						return;
					} else {
						handleSuccess();
					}
				})
				.catch((error) => {
					handleError(error);
				});
		}
	};

	// For Tp Handover Submit only.
	const handleSubmitTPHandover = () => {
		const isValidateFields = validateFormFields(requestType, requestValues, setInputErrors);
		const isValidSampleType = validateSampleTypeInTPHandover(requestValues, setInputErrors);
		const isValidDate = isValidTpHandoverDate(requestValues, setInputErrors);

		if (!isValidateFields || !isValidSampleType || !isValidDate) return;

		if (isSampleDevPurpose(requestValues.handoverPurpose?.constant)) {
			const isValidateSampleDetails = validateSampleDetails(selectedDesigns, setSampleDetailsErrors);
			if (!isValidateSampleDetails && handoverModalFor !== 'TP_HANDOVER_TO_PROJECT_TEAM') {
				toastWarning('Please input Sample Qty.');
				return;
			}
			const isCheckListDone = isCheckListCompleted(selectedDesigns, setSampleDetailsErrors);
			if (!isCheckListDone) {
				toastError('Checklist not completed');
				return;
			}
		}

		const requestBody = {
			productIdList: getSetMembersIds(),
			brandId: requestValues.brand?.id,
			productActionId,
			requiredDate: requestValues?.requiredDate || null,
			expectedOrderDate: requestValues?.expectedOrderDate || null,
			handoverItemRequest: {
				purpose: requestValues?.handoverPurpose?.id,
				projectTag: requestValues?.projectName,
				sampleType: requestValues?.sampleType?.id,
				description: requestValues?.note,
				handoverToUserIdList: requestValues?.handoverToPerson?.map((item) => item?.id),
				...creteSampleRequestBody()
			}
		};

		productDevelopmentAdd({ body: requestBody })
			.unwrap()
			.then((res) => {
				if (!res?.success) {
					if (getSetMembersIds()?.length === 1) {
						const err = Object.values(res?.payload)[0];
						toastError(err || 'Something went wrong');
					} else {
						toastError(res?.message || 'Something went wrong');
					}
					return;
				} else {
					handleSuccess();
				}
			})
			.catch((error) => {
				handleError(error);
			});
	};

	const handleRemoveProduct = (item) => {
		const updatedIDs = productIds?.filter((prodID) => prodID !== item?.id);
		setSelectedProdutIds && setSelectedProdutIds(updatedIDs);

		const updateErrorIds = sampleDetailsErrors.filter((prodId) => prodId !== item?.id);
		setSampleDetailsErrors(updateErrorIds);

		if (updatedIDs?.length === 0) {
			onClose();
		}
	};

	const getSelectedColorSizeQty = (data) => {
		// Get only the first filled row from the first product in this update
		let firstFilledRow = null;
		const allPayloads = Object.values(data);
		if (allPayloads.length > 0 && Array.isArray(allPayloads[0])) {
			const filledRows = allPayloads[0].filter((row) => row?.quantity > 0);
			if (filledRows.length > 0) {
				firstFilledRow = filledRows[0];
			}
		}

		const firstFilledEntry =
			firstFilledRow &&
			({
				quantity: firstFilledRow?.quantity,
				size: firstFilledRow?.size,
				sampleItemDetailsRecipientMapResponse: firstFilledRow?.sampleItemDetailsRecipientMapResponse,
				// Include color data from the first filled row
				code: firstFilledRow?.code || firstFilledRow?.color?.code,
				name: firstFilledRow?.name || firstFilledRow?.color?.name,
				hexCode: firstFilledRow?.hexCode || firstFilledRow?.color?.hexCode,
				pantoneColorId: firstFilledRow?.pantoneColorId || firstFilledRow?.color?.pantoneColorId,
				id: firstFilledRow?.color?.id || null,
				colorDetails: firstFilledRow?.colorDetails
			} ||
				null);

		const updatedDesign = selectedDesigns.map((item) => {
			if (item?.isSet) {
				const newSetItems = item.setItemsList?.map((member) => {
					const keyNum = member?.id;
					const payload = data?.[keyNum] ?? data?.[keyNum?.toString()];

					// Check if this member already has any qty set; if yes, don't auto-overwrite
					const hasExistingQty =
						Array.isArray(member?.colorResponseList) &&
						member.colorResponseList.some((row) => row?.quantity > 0);

					if (payload) {
						// Use only rows with quantity > 0
						const filledRows = payload.filter((row) => row?.quantity > 0);
						return {
							...member,
							colorResponseList: filledRows.map((details) => {
								// Extract color data from details.color if it exists
								const colorData = details?.color || {};

								// eslint-disable-next-line no-unused-vars
								const { colorResponse, color, id, ...restDetails } = details;
								return {
									...restDetails,
									...colorData,

									id: colorData?.id || null,
									quantity: details?.quantity,
									size: details?.size,
									sampleItemDetailsRecipientMapResponse:
										details?.sampleItemDetailsRecipientMapResponse
								};
							})
						};
					}

					// Auto-fill only if member is effectively empty and we have a first filled row
					if (!hasExistingQty && firstFilledEntry) {
						return {
							...member,
							colorResponseList: [firstFilledEntry]
						};
					}

					return member;
				});

				return {
					...item,
					setItemsList: newSetItems
				};
			}

			// Non-set item
			const keyNum = item?.id;
			const payload = data?.[keyNum] ?? data?.[keyNum?.toString()];

			// Check if this item already has any qty set; if yes, don't auto-overwrite
			const hasExistingQty =
				Array.isArray(item?.colorResponseList) &&
				item.colorResponseList.some((row) => row?.quantity > 0);

			if (payload) {
				// Use only rows with quantity > 0
				const filledRows = payload.filter((row) => row?.quantity > 0);
				return {
					...item,
					colorResponseList: filledRows.map((details) => {
						// Extract color data from details.color if it exists
						const colorData = details?.color || {};
						// eslint-disable-next-line no-unused-vars
						const { colorResponse, color, id, ...restDetails } = details;
						return {
							...restDetails,
							...colorData,
							id: colorData?.id || null,
							quantity: details?.quantity,
							size: details?.size,
							sampleItemDetailsRecipientMapResponse:
								details?.sampleItemDetailsRecipientMapResponse
						};
					})
				};
			}

			// Auto-fill only if item is effectively empty and we have a first filled row
			if (!hasExistingQty && firstFilledEntry) {
				return {
					...item,
					colorResponseList: [firstFilledEntry]
				};
			}

			return item;
		});

		setSelectedDesigns(updatedDesign);
	};

	const renderDesignImage = (item) => {
		if (item?.isSet) {
			return (
				<div className="d-flex align-items-center">
					<div className="set-image-layout" style={{ width: ' 56px' }}>
						<CommonImage
							src={item?.designDocuments[0]?.docUrl}
							alt="image"
							className="cell-img pr-2"
							isStatic={false}
						/>
						<CommonImage
							src={item?.designDocuments[1]?.docUrl}
							alt="image"
							className="cell-img pr-2"
							isStatic={false}
						/>
					</div>
					<div className="ml-12">
						{item?.isSet && setTag()}
						<p className="regular-12 gray_dark_01 py-1">{item?.name}</p>
						<ul className="common-list-component mb-2">
							<li>{item?.setItemsList[0]?.productGroup}</li>
							<li>{item?.setItemsList[0]?.category}</li>
							<li>{item?.setItemsList[1]?.category}</li>
						</ul>
					</div>
					<div
						className="action-btn cursor-pointer position-absolute top-0 right-0 d-flex align-items-center justify-content-center"
						onClick={() => handleRemoveProduct(item)}
					>
						<CommonImage src="/icons/close-active.png" width={12} height={12} alt="edit" />
					</div>
				</div>
			);
		}

		return (
			<div className="d-flex align-items-center">
				<CommonImage
					src={item?.designDocuments[0]?.docUrl}
					alt="image"
					className="cell-img"
					isStatic={false}
				/>
				<div className="ml-2">
					{item?.isPack && packTag()}
					<p className="regular-12 gray_dark_01 py-1">{item.referenceNumber}</p>
					<ul className="common-list-component mb-2">
						<li>{item?.productGroup}</li>
						<li>{item?.subCategory}</li>
					</ul>
					{/* <span className="status yellow-1 status-with-border">already sample requested</span> */}
				</div>
				<div
					className="action-btn cursor-pointer position-absolute top-0 right-0 d-flex align-items-center justify-content-center"
					onClick={() => handleRemoveProduct(item)}
				>
					<CommonImage src="/icons/close-active.png" width={12} height={12} alt="edit" />
				</div>
			</div>
		);
	};

	const renderSampleColorPopup = (item, colorResponseList) => {
		return item?.id === designId ? (
			<ColorSizeQtyModalV2
				onClose={() => setDesignId(null)}
				onUpdateSubmit={getSelectedColorSizeQty}
				productId={item?.id}
				requestType={requestType}
				handoverModalFor={handoverModalFor}
				colorResponseList={colorResponseList}
			/>
		) : null;
	};

	const renderSetDetailsColumn = (memberOfSet = []) => {
		const setItMembersList = memberOfSet.slice(1);
		return setItMembersList.map((item) => (
			<tr
				key={item?.id}
				className={
					sampleDetailsErrors.includes(item.id)
						? 'position-relative row-error'
						: 'position-relative'
				}
			>
				<td onClick={() => setDesignId(item?.id)}>
					<div className="sample-details">
						{item?.colorResponseList.filter((color) => color.size || color.quantity).length ===
							0 && <p>Add size, qty & color</p>}
						{item?.colorResponseList?.map(
							(color) =>
								(color.size || color.quantity) && (
									<ul
										className="common-list-component align-items-center mb-2"
										key={`${color.id}`}
									>
										<li className="d-flex align-items-center">
											{color.hexCode && (
												<span
													className="color-icon"
													style={{ background: color.hexCode }}
												/>
											)}
											<span className="ml-1">{color.code || color.name}</span>
										</li>

										<li>
											{(color?.size || color.quantity) && (
												<span>
													{color?.size} {color?.size && '-'} {color?.quantity} PCS
												</span>
											)}
										</li>
										<li>
											{color?.sampleItemDetailsRecipientMapResponse?.length > 0 &&
												color?.sampleItemDetailsRecipientMapResponse?.map(
													(user, index) => (
														<span className="mr-1 ml-1" key={user?.id}>
															{user?.userResponse?.name || user?.name}
															{color?.sampleItemDetailsRecipientMapResponse
																?.length > 1 &&
															index <
																color?.sampleItemDetailsRecipientMapResponse
																	?.length -
																	1
																? ','
																: ''}
														</span>
													)
												)}
										</li>
									</ul>
								)
						)}
					</div>
					{renderSampleColorPopup(item, item.colorResponseList)}
				</td>
				<td>
					<div className="status">
						<span className="status green-3">Completed</span>
					</div>
				</td>
				{requestType === SAMPLE_REQUEST_TYPES.SAMPLE && (
					<td>
						<div className="status">
							<span className={`status ${TECHPACK_STATUS_TAG_COLORS[item?.checklistStatus]}`}>
								{item?.checklistStatus}
							</span>
						</div>
					</td>
				)}
			</tr>
		));
	};

	const getUsersByType = (usersResponse) => {
		const newUsersArr = [];
		Object.keys(usersResponse).forEach((userType) => {
			newUsersArr.push(...usersResponse[userType]);
		});

		return newUsersArr;
	};

	const isProjectManager = () => checkUserType('PROJECT_MANAGER');
	const isAccountManager = () => checkUserType('ACCOUNT_MANAGER');
	const isBusinessDevelopmentManager = () => checkUserType('BUSINESS_DEVELOPMENT_MANAGER');
	const isFashionDesigner = () => checkUserType('FASHION_DESIGNER');
	const isGraphicDesigner = () => checkUserType('GRAPHIC_DESIGNER');

	const renderForm = () => {
		const formProps = {
			handleChange,
			requestValues,
			errors: inputErrors
		};

		switch (requestType) {
			case SAMPLE_REQUEST_TYPES.SEND_TO_PD_IN_HOUSE:
				return (
					<SendToPdForm
						{...formProps}
						inputErrors={inputErrors}
						brands={brands}
						projectList={projectList}
						sampleTypeList={sampleTypeList}
						userList={userList}
						selectedComment={selectedComment}
					/>
				);
			case SAMPLE_REQUEST_TYPES.SEND_TO_FACTORY_NITEX_DESIGN:
				return <SendToFactoryNitexDevForm {...formProps} sampleTypeList={sampleTypeList} />;
			case SAMPLE_REQUEST_TYPES.SEND_TO_FACTORY_BUYER_DESIGN:
				return (
					<SendToFactoryBuyerDevForm
						{...formProps}
						brands={brands}
						sampleTypeList={sampleTypeList}
						projectList={projectList}
						isDisabeleBrand={isEdit}
						isDisabledProjectTeam={isProjectManager()}
						isDisabledProjectAndAm={isAccountManager() || isBusinessDevelopmentManager()}
					/>
				);
			case SAMPLE_REQUEST_TYPES.SAMPLE:
			case SAMPLE_REQUEST_TYPES.SEND_REVISE_SAMPLE_REQUEST:
				return (
					<SendToSampleSectionForm
						{...formProps}
						sampleTypeList={sampleTypeList}
						isReviseSample={requestType === SAMPLE_REQUEST_TYPES.SEND_REVISE_SAMPLE_REQUEST}
					/>
				);

			case SAMPLE_REQUEST_TYPES.TP_HANDOVER:
			case SAMPLE_REQUEST_TYPES.TP_HANDOVER_TO_PROJECT_TEAM:
			case SAMPLE_REQUEST_TYPES.TP_HANDOVER_TO_PATTERN_SECTION:
			case SAMPLE_REQUEST_TYPES.TP_HANDOVER_TO_ACCOUNT_MANAGER:
			case SAMPLE_REQUEST_TYPES.TP_HANDOVER_TO_DESIGNER:
				return (
					<TechpackHandover
						{...formProps}
						sampleTypeList={sampleTypeList}
						brands={brands}
						projectList={projectList}
						userList={getUsersByType(userList)}
						handoverType={requestType}
						handoverModalFor={handoverModalFor}
						brandMembers={brandMembers}
					/>
				);
			default:
				return null;
		}
	};
	const renderDesignManagerApprovalStatus = (item) => {
		if (
			!('designManagerApproved' in item) ||
			requestValues.sampleType?.constant !== 'NITEX_DEVELOPMENT'
		) {
			return <span className="status gray-1">Not required</span>;
		}
		if (
			item.designManagerApproved === true &&
			requestValues.sampleType?.constant === 'NITEX_DEVELOPMENT'
		) {
			return <span className="status green-3">Approved</span>;
		}
		return <span className="status red-3">Not approved</span>;
	};

	return (
		<LoaderComponent
			loading={
				isSubmitLoading ||
				isSampleUpdateLoading ||
				isUpdateSampleRequestLoading ||
				isTPHandoverSubmitting
			}
		>
			<ModalContainer className="sample-request-modal width-720-modal">
				<div className="common-popup sample-request-modal add-sample-details-popup shadow-16dp">
					<ModalHeader title={title} onClose={onClose} />
					<div className="common-popup-body scroll-y-label">
						<div className="send-information-flied">{renderForm()}</div>
						<div className="send-pd-table sample-details-table scroll-x-label">
							<table className="table with-border">
								<thead>
									<tr>
										<th className="design-details-column">
											<span className="ml-2">Design({selectedDesigns?.length})</span>
										</th>
										{isShowProductDetailsOnTable(
											taskValueType,
											requestValues.handoverPurpose?.id
										) && (
											<>
												<th className="sample-details-column">
													Sample details <br />
													<span className="regular-12 gray_dark_02">
														(Color, Size, Qty, Whom to send)
													</span>
												</th>
												<th className="sample-check-column">Checklist*</th>
											</>
										)}
										{requestType === SAMPLE_REQUEST_TYPES.SAMPLE && (
											<th className="sample-details-column">Preveiously Dev?</th>
										)}
										{(isFashionDesigner() || isGraphicDesigner()) &&
											requestType === 'SEND_TO_PD' && (
												<th className="sample-check-column">Team lead approval*</th>
											)}
										<th className="sample-details-column remarks-column">Remarks</th>
									</tr>
								</thead>
								<tbody>
									{selectedDesigns.map((item) => (
										<React.Fragment key={item.id}>
											<tr
												className={
													sampleDetailsErrors.includes(item.id) ? 'row-error' : ''
												}
											>
												<td className="design-column" rowSpan={item?.setMemberCount}>
													{renderDesignImage(item)}
												</td>
												{isShowProductDetailsOnTable(
													taskValueType,
													requestValues.handoverPurpose?.id
												) && (
													<td
														onClick={() =>
															setDesignId(
																item?.isSet
																	? item?.setItemsList[0]?.id
																	: item?.id
															)
														}
													>
														<div className="sample-details">
															{(() => {
																const colorResponse = item?.isSet
																	? item?.setItemsList?.[0]
																			?.colorResponseList
																	: item?.colorResponseList;
																return (
																	<>
																		{colorResponse?.filter(
																			(color) =>
																				color?.size || color?.quantity
																		).length === 0 && (
																			<p>Add size, qty & color</p>
																		)}
																		{colorResponse?.map(
																			(color) =>
																				(color?.size ||
																					color?.quantity) && (
																					<ul
																						className="common-list-component align-items-center mb-2"
																						key={
																							color?.key ||
																							color?.id
																						}
																					>
																						<li className="d-flex align-items-center">
																							{color.hexCode && (
																								<span
																									className="color-icon"
																									style={{
																										background:
																											color.hexCode
																									}}
																								/>
																							)}
																							<span>
																								{color.code ||
																									color.name}
																							</span>
																						</li>

																						<li className="">
																							{(color?.size ||
																								color.quantity) && (
																								<span>
																									{
																										color?.size
																									}{' '}
																									{color?.size &&
																										'-'}{' '}
																									{
																										color?.quantity
																									}{' '}
																									PCS
																								</span>
																							)}
																						</li>
																						<li>
																							{color
																								?.sampleItemDetailsRecipientMapResponse
																								?.length >
																								0 &&
																								color?.sampleItemDetailsRecipientMapResponse?.map(
																									(
																										user,
																										index
																									) => (
																										<span
																											className="mr-1 ml-1"
																											key={
																												user?.id
																											}
																										>
																											{user
																												?.userResponse
																												?.name ||
																												user?.name}{' '}
																											{color
																												?.sampleItemDetailsRecipientMapResponse
																												?.length >
																												1 &&
																											index <
																												color
																													?.sampleItemDetailsRecipientMapResponse
																													?.length -
																													1
																												? ','
																												: ''}
																										</span>
																									)
																								)}
																						</li>
																					</ul>
																				)
																		)}
																	</>
																);
															})()}
														</div>
														{renderSampleColorPopup(
															item?.isSet ? item?.setItemsList[0] : item,
															item?.isSet
																? item?.setItemsList[0]?.colorResponseList
																: item.colorResponseList
														)}
													</td>
												)}
												{isShowProductDetailsOnTable(
													taskValueType,
													requestValues.handoverPurpose?.id
												) && (
													<td>
														<div className="status">
															<span
																className={`status ${
																	TECHPACK_STATUS_TAG_COLORS[
																		item?.isSet
																			? item?.setItemsList[0]
																					?.checklistStatus
																			: item?.checklistStatus
																	]
																}`}
															>
																{item?.isSet
																	? item?.setItemsList[0]?.checklistStatus
																	: item?.checklistStatus}
															</span>
														</div>
													</td>
												)}

												{requestType === SAMPLE_REQUEST_TYPES.SAMPLE && (
													<td>
														<div className="status">
															{item?.completedSampleCount > 0 ? (
																<span className="status green-1">
																	Complete
																</span>
															) : (
																<span className="status gray-1">
																	Incomplete
																</span>
															)}
														</div>
													</td>
												)}
												{(isFashionDesigner() || isGraphicDesigner()) &&
													requestType === 'SEND_TO_PD' && (
														<td>
															{renderDesignManagerApprovalStatus(
																item,
																requestValues.sampleType?.constant
															)}
														</td>
													)}
												<td
													className="remarks-column"
													onClick={() => {
														if (selectedRowId === null) {
															setSelectedRowId(item.id);
														}
													}}
													rowSpan={item?.setMemberCount}
												>
													{selectedRowId === item.id ? (
														<Textarea
															autoFocus={true}
															type="text"
															autoComplete="off"
															name="remarks"
															value={item.remarks || ''}
															onChange={handleRemarks}
															onBlur={() => setSelectedRowId(null)}
														/>
													) : (
														item.remarks
													)}
												</td>
											</tr>
											{item?.isSet && renderSetDetailsColumn(item?.setItemsList)}
										</React.Fragment>
									))}
								</tbody>
							</table>
						</div>
					</div>

					<ModalFooter
						onSubmit={isHandoverModal ? handleSubmitTPHandover : handleSubmit}
						disabled={
							isSubmitLoading ||
							isSampleUpdateLoading ||
							isUpdateSampleRequestLoading ||
							isTPHandoverSubmitting
						}
					/>
				</div>
			</ModalContainer>
		</LoaderComponent>
	);
};

export default React.memo(SampleRequestModal);
